import { useHistory } from "react-router-dom";

export const sortArrayByProp = (
  array: any,
  property: string,
  direction = "ASC"
): any[] => {
  const hasProps = array?.find((obj: any) => obj.hasOwnProperty(property));
  if (!hasProps) {
    return array;
  }

  array.sort((a: any, b: any) => {
    const valorA = a[property];
    const valorB = b[property];

    if (direction === "DESC") {
      if (valorA < valorB) return 1;
      if (valorA > valorB) return -1;
    } else {
      if (valorA < valorB) return -1;
      if (valorA > valorB) return 1;
    }
    return 0;
  });

  return array;
};

export const sortArrayByDirection = (
  array: any[],
  direction = "ASC"
): any[] => {
  if (!array?.length) {
    return array;
  }
  array.sort((a, b) => {
    const valorA = a;
    const valorB = b;
    if (direction === "DESC") {
      if (valorA < valorB) return 1;
      if (valorA > valorB) return -1;
    } else {
      if (valorA < valorB) return -1;
      if (valorA > valorB) return 1;
    }
    return 0;
  });

  return array;
};

type ObjectType = {
  [key: string]: any;
};

export function removeEmptyProperties(
  obj: ObjectType,
  keepThese: string[] = []
): ObjectType {
  return Object.entries(obj)
    .filter(([key, value]) => {
      if (keepThese.includes(key)) {
        return true;
      }
      if (typeof value === "object" && value !== null) {
        const nested = removeEmptyProperties(value, keepThese);
        return Object.keys(nested).length !== 0;
      }
      return value !== "" && value !== null && value !== undefined;
    })
    .reduce((acc: ObjectType, [key, value]) => {
      return { ...acc, [key]: value };
    }, {});
}

type UpdateFilterProps = {
  displayField?: any;
  property: string;
  value: any;
  isArrayValue?: boolean;
  history?: ReturnType<typeof useHistory>;
};

type FilterParams = {
  setBodyFilters: (filters: UpdateFilterProps) => void;
  urlSearchParams: URLSearchParams;
  filterOptions: any;
  urlParam: string;
  fieldToSearch?: string;
  fieldToCompare?: string;
  returnAsArray?: boolean;
};

export function filterParamsByUrlField({
  setBodyFilters,
  urlSearchParams,
  filterOptions,
  urlParam,
  fieldToSearch,
  fieldToCompare,
  returnAsArray,
}: FilterParams) {
  const fieldParam = urlSearchParams.get(urlParam);
  if (!fieldParam) return;

  const searchProp = fieldToSearch ?? urlParam;

  const filteredFilterOption = filterOptions[searchProp]?.find((item: any) =>
    fieldToCompare && item.hasOwnProperty(fieldToCompare)
      ? item[fieldToCompare].toString() === fieldParam
      : item.toString() === fieldParam
  );
  const selectedField = filteredFilterOption ?? fieldParam;

  if (selectedField) {
    setBodyFilters({
      property: urlParam,
      value: selectedField,
      isArrayValue: returnAsArray,
    });
  }
}

export type SelectArrayOption = {
  value: string;
  label: string;
};

export function transformArray(
  array: any,
  labels: Record<string, string>
): SelectArrayOption[] {
  if (!array) return [];

  return array.map((option: any) => ({
    value: option,
    label: labels[option] || option,
  }));
}


export const parseStringToJson = (text: string) => {
  try {
    return JSON.parse(text);
  } catch (error) {
    return text;
  }
};

export function removeEmpty(obj: any, dontRemoveThis: string[] = []): any {
  return Object.entries(obj)
      .filter(([key , value]) => {
        if (dontRemoveThis.includes(key)) {
          return true;
        }
        if (typeof value === 'object' && value !== null) {
          const nested = removeEmpty(value, dontRemoveThis);
          return Object.keys(nested).length !== 0;
        }
        return value !== '' && value !== null && value !== undefined;
      })
      .reduce((acc, [key, value]) => {
        return {...acc, [key]: value};
      }, {});
}


export const formatStringArray = (value: any = '') : any => {
  if (!value  || (typeof value) !== "string") return '';
  return value
      .replaceAll('_', ' ')
      .split(' ')
      .map((val: string, index: number) =>  index === 0 || val.length > 4 ? val.toLowerCase() : val)
      .map((val: string) => val.charAt(0).toUpperCase() + val.slice(1))
      .join(' ');
};

export const formatStringTime = (time: string) => {
  if (/^\d{2}:\d{2}:\d{2}$/.test(time)) {
    return time;
  }
  return `${time}:00`;
};

export function onlyNumbers(value: string) {
  const regexCNPJ = /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/;
  if (regexCNPJ.test(value)) {
    return value.replace(/\D/g, '');
  }
  return value
}

export const sortByDirection = <T extends Record<string, any>>(
    array: T[],
    property: string | null = null,
    direction: 'ASC' | 'DESC' = 'ASC'
): T[] => {
  if (!array?.length) return [];

  if (property && !array.some(obj => property in obj)) {
    return array; // Retorna o array original se a propriedade não existir em nenhum objeto
  }

  return array.sort((a, b) => {
    const valorA = property ? (a[property] ?? '').toString() : a.toString();
    const valorB = property ? (b[property] ?? '').toString() : b.toString();

    const comparison = valorA.localeCompare(valorB, undefined, { sensitivity: 'base' });
    return direction === 'DESC' ? -comparison : comparison;
  });
};

export function capitalizeWords(name: string) {
  if (!name) return;
  const words = name.replace(/\s+/g, ' ').trim().toLowerCase().split(' ');
  return words.map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}